import React, { useState, useEffect, useCallback } from 'react';
import {
  BanknotesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { getBankAvailability } from '../api/client';
import { serverDate } from '../utils/formatters';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMoney(val, currency) {
  if (val === null || val === undefined) return '—';
  const abs = Math.abs(val);
  const formatted =
    currency === 'USD'
      ? `$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `Bs ${abs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return val < 0 ? `-${formatted}` : formatted;
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function timeSince(dateStr) {
  if (!dateStr) return null;
  try {
    const diff = Date.now() - serverDate(dateStr).getTime();
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'hoy';
    if (days === 1) return 'hace 1 día';
    return `hace ${days} días`;
  } catch {
    return null;
  }
}

// Bank brand colors (for the colored top-bar on each card)
const BANK_COLORS = {
  banplus:  '#1E40AF',  // dark blue
  bdv:      '#DC2626',  // red
  bnc:      '#D97706',  // amber
  banesco:  '#7C3AED',  // purple
  mercantil:'#059669',  // green
  venezolano:'#0891B2', // cyan
  bicentenario:'#BE185D',
};

function bankColor(name = '') {
  const lower = name.toLowerCase();
  for (const [key, color] of Object.entries(BANK_COLORS)) {
    if (lower.includes(key)) return color;
  }
  return '#475569'; // slate default
}

// ─── AccountCard ─────────────────────────────────────────────────────────────

function AccountCard({ account }) {
  const {
    bank_name, account_name, currency,
    saldo_final, saldo_inicial,
    continuity_ok, continuity_diff,
    period_end, upload_date,
    total_ingresos, total_egresos,
  } = account;

  const color      = bankColor(bank_name);
  const isAlert    = continuity_ok === false;
  const isOk       = continuity_ok === true;
  const noHistory  = continuity_ok === null || continuity_ok === undefined;
  const dataAge    = timeSince(upload_date);
  const stale      = (() => {
    if (!upload_date) return false;
    const days = (Date.now() - serverDate(upload_date).getTime()) / 86400000;
    return days > 7;
  })();

  return (
    <div className={`bg-white rounded-xl border-2 ${
      isAlert ? 'border-red-300 shadow-red-100' : stale ? 'border-amber-200' : 'border-gray-200'
    } shadow-sm overflow-hidden flex flex-col`}>
      {/* Top color bar */}
      <div style={{ background: color, height: '4px' }} />

      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white"
            style={{ background: color }}
          >
            <BuildingLibraryIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{bank_name}</p>
            <p className="text-xs text-gray-500 truncate">{account_name}</p>
          </div>
        </div>

        {/* Currency + continuity badge */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
            currency === 'USD'
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}>
            {currency}
          </span>
          {isOk && (
            <span className="flex items-center gap-1 text-xs text-green-700">
              <CheckCircleIcon className="w-3.5 h-3.5" />
              OK
            </span>
          )}
          {isAlert && (
            <span className="flex items-center gap-1 text-xs text-red-700 font-semibold">
              <ExclamationTriangleIcon className="w-3.5 h-3.5" />
              Alerta
            </span>
          )}
          {noHistory && (
            <span className="text-xs text-gray-400">Sin historial</span>
          )}
        </div>
      </div>

      {/* Big balance */}
      <div className="px-5 pb-3">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">
          Saldo disponible
        </p>
        <p className={`text-2xl font-black ${
          saldo_final < 0 ? 'text-red-600' : 'text-gray-900'
        }`}>
          {fmtMoney(saldo_final, currency)}
        </p>
        {period_end && (
          <p className="text-xs text-gray-400 mt-0.5">
            Al {fmtDate(period_end)}
          </p>
        )}
      </div>

      {/* Alert: continuity problem */}
      {isAlert && (
        <div className="mx-5 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          <p className="text-xs font-semibold text-red-700 flex items-center gap-1 mb-0.5">
            <ExclamationTriangleIcon className="w-3.5 h-3.5" /> Discontinuidad detectada
          </p>
          <p className="text-xs text-red-600">
            Diferencia de {fmtMoney(continuity_diff, currency)} — el saldo inicial del último PDF
            no coincide con el saldo final anterior. Puede faltar un estado de cuenta intermedio.
          </p>
        </div>
      )}

      {/* Stale data warning */}
      {stale && !isAlert && (
        <div className="mx-5 mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <p className="text-xs text-amber-700 flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            Datos desactualizados — cargado {dataAge}
          </p>
        </div>
      )}

      {/* Divider */}
      <div className="mx-5 border-t border-gray-100" />

      {/* Footer stats */}
      <div className="px-5 py-3 grid grid-cols-2 gap-2">
        <div>
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <ArrowTrendingUpIcon className="w-3 h-3 text-green-500" /> Ingresos período
          </p>
          <p className="text-sm font-semibold text-gray-700">
            {fmtMoney(total_ingresos, currency)}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <ArrowTrendingDownIcon className="w-3 h-3 text-red-500" /> Egresos período
          </p>
          <p className="text-sm font-semibold text-gray-700">
            {fmtMoney(total_egresos, currency)}
          </p>
        </div>
      </div>

      {/* Upload timestamp */}
      {upload_date && (
        <div className="px-5 pb-3">
          <p className="text-xs text-gray-400">
            <ClockIcon className="w-3 h-3 inline mr-1" />
            Actualizado {dataAge}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── SummaryCard ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, sub, accent }) {
  const colors = {
    green:  'bg-green-50  border-green-200  text-green-800',
    blue:   'bg-blue-50   border-blue-200   text-blue-800',
    red:    'bg-red-50    border-red-200    text-red-800',
    slate:  'bg-slate-50  border-slate-200  text-slate-700',
  };
  return (
    <div className={`rounded-xl border px-5 py-4 ${colors[accent] || colors.slate}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BankAvailability() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [filter,     setFilter]     = useState('all'); // 'all' | 'usd' | 'ves' | 'alerts'

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getBankAvailability();
      setData(result);
      setLastFetch(new Date());
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar la disponibilidad bancaria.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  const accounts = data?.accounts || [];
  const filtered = accounts.filter(a => {
    if (filter === 'usd')    return a.currency === 'USD';
    if (filter === 'ves')    return a.currency === 'VES';
    if (filter === 'alerts') return a.continuity_ok === false;
    return true;
  });

  const alertCount = accounts.filter(a => a.continuity_ok === false).length;
  const staleCount = accounts.filter(a => {
    if (!a.upload_date) return false;
    return (Date.now() - serverDate(a.upload_date).getTime()) / 86400000 > 7;
  }).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BanknotesIcon className="w-7 h-7 text-blue-600" />
            Disponibilidad Bancaria
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Saldos actuales por cuenta · Se actualiza cada 5 min
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetch && (
            <span className="text-xs text-gray-400">
              Última actualización: {lastFetch.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Error al cargar datos</p>
            <p className="text-red-700 text-xs mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Alert banner */}
      {!loading && data?.has_alerts && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-xl px-5 py-4 flex items-start gap-4">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-800">
              {alertCount} cuenta{alertCount > 1 ? 's' : ''} con discontinuidad detectada
            </p>
            <p className="text-sm text-red-700 mt-1">
              El saldo inicial de uno o más PDFs no coincide con el saldo final anterior.
              Esto indica que puede faltar un estado de cuenta intermedio.
              Sube los estados bancarios faltantes en <strong>Conciliación Bancaria</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Stale data warning */}
      {!loading && staleCount > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center gap-3">
          <ClockIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{staleCount} cuenta{staleCount > 1 ? 's' : ''}</strong> tienen datos con más de 7 días.
            Sube los estados bancarios más recientes.
          </p>
        </div>
      )}

      {/* Summary cards */}
      {!loading && data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label="Total USD"
            value={`$${(data.total_usd || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub={`${accounts.filter(a => a.currency === 'USD').length} cuentas`}
            accent="green"
          />
          <SummaryCard
            label="Total VES"
            value={`Bs ${(data.total_ves || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            sub={`${accounts.filter(a => a.currency === 'VES').length} cuentas`}
            accent="blue"
          />
          <SummaryCard
            label="Cuentas"
            value={accounts.length}
            sub="bancos monitoreados"
            accent="slate"
          />
          <SummaryCard
            label="Alertas"
            value={alertCount}
            sub={alertCount === 0 ? 'todo en orden' : 'revisar continuidad'}
            accent={alertCount > 0 ? 'red' : 'slate'}
          />
        </div>
      )}

      {/* Filter tabs */}
      {!loading && accounts.length > 0 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          {[
            { key: 'all',    label: `Todas (${accounts.length})` },
            { key: 'usd',    label: `USD (${accounts.filter(a => a.currency === 'USD').length})` },
            { key: 'ves',    label: `VES (${accounts.filter(a => a.currency === 'VES').length})` },
            ...(alertCount > 0 ? [{ key: 'alerts', label: `⚠ Alertas (${alertCount})` }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl border-2 border-gray-200 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-full mb-1" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && accounts.length === 0 && !error && (
        <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">Sin datos bancarios</h3>
          <p className="text-gray-500 text-sm max-w-sm mx-auto">
            No hay estados de cuenta cargados. Sube un PDF en{' '}
            <a href="/bank-reconciliation" className="text-blue-600 hover:underline">
              Conciliación Bancaria
            </a>{' '}
            para comenzar.
          </p>
        </div>
      )}

      {/* Accounts grid */}
      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((account, i) => (
            <AccountCard key={`${account.bank_name}-${account.account_name}-${i}`} account={account} />
          ))}
        </div>
      )}

      {/* Empty filter result */}
      {!loading && accounts.length > 0 && filtered.length === 0 && (
        <div className="text-center py-10 text-gray-400 text-sm">
          No hay cuentas con el filtro seleccionado.
        </div>
      )}
    </div>
  );
}
